import '../styles/PortfolioUpdates.css';
import React, { useMemo } from 'react';

const PortfolioUpdates = ({ 
    portfolio = [], 
    performanceData = {}, 
    portfolioStats = {}, 
    preferredCurrency = 'USD' 
}) => {
    const formatPercent = (value) => {
        return `${Math.abs(value).toFixed(1)}%`;
    };

    const generateUpdates = useMemo(() => {
        const updates = [];
        const now = new Date();

        // Top performer update
        if (portfolio.length > 0) {
            const topPerformer = portfolio.reduce((best, stock) => 
                (stock.growth || 0) > (best.growth || 0) ? stock : best
            );

            if (topPerformer.growth > 5) {
                updates.push({
                    id: 'top-performer',
                    type: 'success',
                    icon: 'fa-chart-line',
                    message: `${topPerformer.name} ist dein Top-Performer mit +${formatPercent(topPerformer.growth)} Gewinn seit Kauf`,
                    timestamp: now,
                    priority: 1
                });
            }
        }

        // Worst performer update
        if (portfolio.length > 0) {
            const worstPerformer = portfolio.reduce((worst, stock) => 
                (stock.growth || 0) < (worst.growth || 0) ? stock : worst
            );

            if (worstPerformer.growth < -10) {
                updates.push({
                    id: 'worst-performer',
                    type: 'warning',
                    icon: 'fa-arrow-trend-down',
                    message: `${worstPerformer.name} hat ${formatPercent(worstPerformer.growth)} seit Kauf verloren`,
                    timestamp: now,
                    priority: 2
                });
            }
        }

        // High dividend stocks update (with names)
        const highDividendStocks = portfolio.filter(stock => 
            (stock.dividend_yield || 0) > 4
        );

        if (highDividendStocks.length > 0) {
            const stockNames = highDividendStocks.slice(0, 3).map(s => s.name).join(', ');
            const moreText = highDividendStocks.length > 3 ? ` (+${highDividendStocks.length - 3} weitere)` : '';
            
            updates.push({
                id: 'high-dividend',
                type: 'info',
                icon: 'fa-coins',
                message: `Hohe Dividenden: ${stockNames}${moreText} über 4% Rendite`,
                timestamp: now,
                priority: 3
            });
        }

        // Portfolio concentration update
        if (portfolio.length > 0) {
            const totalValue = portfolioStats.totalValue || 0;
            const largestPosition = portfolio.reduce((largest, stock) => 
                (stock.total_value || 0) > (largest.total_value || 0) ? stock : largest
            );

            const concentration = totalValue > 0 ? (largestPosition.total_value / totalValue) * 100 : 0;

            if (concentration > 25) {
                updates.push({
                    id: 'concentration-risk',
                    type: 'warning',
                    icon: 'fa-exclamation-triangle',
                    message: `${largestPosition.name} macht ${concentration.toFixed(1)}% deines Portfolios aus - eventuell diversifizieren?`,
                    timestamp: now,
                    priority: 2
                });
            }
        }

        // Sector concentration analysis
        if (portfolio.length > 1) {
            const sectorMap = portfolio.reduce((acc, stock) => {
                const sector = stock.sector || 'Unbekannt';
                acc[sector] = (acc[sector] || 0) + (stock.total_value || 0);
                return acc;
            }, {});

            const totalValue = portfolioStats.totalValue || 0;
            const dominantSector = Object.entries(sectorMap).reduce((max, [sector, value]) => 
                value > max.value ? { sector, value } : max, { sector: '', value: 0 }
            );

            const sectorConcentration = totalValue > 0 ? (dominantSector.value / totalValue) * 100 : 0;

            if (sectorConcentration > 40) {
                updates.push({
                    id: 'sector-concentration',
                    type: 'info',
                    icon: 'fa-industry',
                    message: `${dominantSector.sector} dominiert mit ${sectorConcentration.toFixed(1)}% - Sektordiversifikation prüfen`,
                    timestamp: now,
                    priority: 3
                });
            }
        }

        // Portfolio P/E analysis
        const peStocks = portfolio.filter(stock => (stock.trailing_p_e || 0) > 0);
        if (peStocks.length > 0) {
            const avgPE = peStocks.reduce((sum, stock) => sum + (stock.trailing_p_e || 0), 0) / peStocks.length;
            
            if (avgPE < 15) {
                updates.push({
                    id: 'portfolio-pe-low',
                    type: 'success',
                    icon: 'fa-balance-scale',
                    message: `Durchschnittliches KGV von ${avgPE.toFixed(1)} - dein Portfolio ist günstig bewertet`,
                    timestamp: now,
                    priority: 2
                });
            } else if (avgPE > 25) {
                updates.push({
                    id: 'portfolio-pe-high',
                    type: 'warning',
                    icon: 'fa-balance-scale',
                    message: `Durchschnittliches KGV von ${avgPE.toFixed(1)} - Portfolio könnte überbewertet sein`,
                    timestamp: now,
                    priority: 2
                });
            }
        }

        // Weekly significant movers (with names)
        const weeklyGainers = portfolio.filter(stock => (stock.weekly_change || 0) > 15);
        const weeklyLosers = portfolio.filter(stock => (stock.weekly_change || 0) < -15);

        if (weeklyGainers.length > 0) {
            const stockNames = weeklyGainers.slice(0, 3).map(s => s.name).join(', ');
            const moreText = weeklyGainers.length > 3 ? ` (+${weeklyGainers.length - 3})` : '';
            
            updates.push({
                id: 'weekly-gainers',
                type: 'success',
                icon: 'fa-arrow-trend-up',
                message: `Wochengewinner: ${stockNames}${moreText} über +15%`,
                timestamp: now,
                priority: 1
            });
        }

        if (weeklyLosers.length > 0) {
            const stockNames = weeklyLosers.slice(0, 3).map(s => s.name).join(', ');
            const moreText = weeklyLosers.length > 3 ? ` (+${weeklyLosers.length - 3})` : '';
            
            updates.push({
                id: 'weekly-losers',
                type: 'danger',
                icon: 'fa-arrow-trend-down',
                message: `Wochenverlierer: ${stockNames}${moreText} unter -15%`,
                timestamp: now,
                priority: 2
            });
        }

        // Portfolio diversification score
        if (portfolio.length > 3) {
            const uniqueSectors = new Set(portfolio.map(stock => stock.sector)).size;
            const diversificationScore = (uniqueSectors / portfolio.length) * 100;
            
            if (diversificationScore > 60) {
                updates.push({
                    id: 'good-diversification',
                    type: 'success',
                    icon: 'fa-chart-pie',
                    message: `Gute Diversifikation: ${uniqueSectors} Sektoren bei ${portfolio.length} Aktien`,
                    timestamp: now,
                    priority: 3
                });
            } else if (diversificationScore < 30) {
                updates.push({
                    id: 'poor-diversification',
                    type: 'warning',
                    icon: 'fa-chart-pie',
                    message: `Niedrige Diversifikation: nur ${uniqueSectors} Sektoren bei ${portfolio.length} Aktien`,
                    timestamp: now,
                    priority: 2
                });
            }
        }

        // Cash flow potential
        const cashFlowStocks = portfolio.filter(stock => (stock.free_cash_flow || 0) > 0);
        if (cashFlowStocks.length > portfolio.length * 0.7) {
            updates.push({
                id: 'strong-cash-flow',
                type: 'info',
                icon: 'fa-money-bill-wave',
                message: `${cashFlowStocks.length} von ${portfolio.length} Aktien haben positiven Free Cash Flow`,
                timestamp: now,
                priority: 3
            });
        }

        // Growth vs Value balance
        const growthStocks = portfolio.filter(stock => (stock.growth_estimates?.next_year || 0) > 0.15);
        const valueStocks = portfolio.filter(stock => (stock.trailing_p_e || 0) > 0 && (stock.trailing_p_e || 0) < 15);
        
        if (growthStocks.length > valueStocks.length * 2) {
            updates.push({
                id: 'growth-heavy',
                type: 'info',
                icon: 'fa-seedling',
                message: `Portfolio ist wachstumslastig: ${growthStocks.length} Growth vs ${valueStocks.length} Value-Aktien`,
                timestamp: now,
                priority: 3
            });
        } else if (valueStocks.length > growthStocks.length * 2) {
            updates.push({
                id: 'value-heavy',
                type: 'info',
                icon: 'fa-tags',
                message: `Portfolio ist wertorientiert: ${valueStocks.length} Value vs ${growthStocks.length} Growth-Aktien`,
                timestamp: now,
                priority: 3
            });
        }

        // Small positions analysis
        const smallPositions = portfolio.filter(stock => {
            const positionSize = ((stock.total_value || 0) / (portfolioStats.totalValue || 1)) * 100;
            return positionSize < 2;
        });

        if (smallPositions.length > 3) {
            updates.push({
                id: 'many-small-positions',
                type: 'info',
                icon: 'fa-coins',
                message: `${smallPositions.length} Positionen unter 2% - eventuell konsolidieren?`,
                timestamp: now,
                priority: 3
            });
        }

        // Sort by priority and timestamp, limit to 12
        return updates
            .sort((a, b) => a.priority - b.priority || b.timestamp - a.timestamp)
            .slice(0, 12);

    }, [portfolio, portfolioStats]);

    const getUpdateTypeClass = (type) => {
        switch (type) {
            case 'success': return 'positive';
            case 'danger': return 'negative';
            case 'warning': return 'negative';
            default: return '';
        }
    };

    const getUpdateIcon = (type) => {
        switch (type) {
            case 'success': return 'var(--success-color)';
            case 'danger': return 'var(--danger-color)';
            case 'warning': return '#f97316';
            default: return 'var(--text-secondary)';
        }
    };

    if (generateUpdates.length === 0) {
        return (
            <div className="no-updates-message">
                <i className="fas fa-info-circle"></i>
                <p>Noch keine Updates verfügbar.</p>
            </div>
        );
    }

    return (
        <div className="updates-container">
            <div className="updates-list">
                {generateUpdates.map((update) => (
                    <div key={update.id} className="update-item">
                        <div className="update-icon">
                            <i 
                                className={`fas ${update.icon}`}
                                style={{ color: getUpdateIcon(update.type) }}
                            ></i>
                        </div>
                        <div className="update-content">
                            <p className={`update-message ${getUpdateTypeClass(update.type)}`}>
                                {update.message}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PortfolioUpdates;